import { FC, useCallback } from 'react'
import { IconButton } from '@cargomart/ui'
import IconFile from 'assets/file-icon.svg?react'
import { notify } from 'components/extend/Notification/NotificationEmitter'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { TOrderPayment } from 'modules/order/types'
import IconFileCopy from 'remixicon/icons/Document/file-copy-line.svg?react'
import { OrderAccess, OrderItem } from 'schema'
import { getEnv } from 'src/helpers/common'

import { OrderPaymentBlock, PaymentForm } from '../OrderPaymentBlock/OrderPaymentBlock'

interface OrderPaymentSectionProps {
  isDocumentInvoiceLoading: boolean
  isFetching: boolean
  isPaymentLoading: boolean
  order: OrderItem
  payment: TOrderPayment[] | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  docInvoice?: any
}

export const OrderPaymentSection: FC<OrderPaymentSectionProps> = ({
  order,
  payment,
  isPaymentLoading,
  isFetching,
  docInvoice,
  isDocumentInvoiceLoading
}) => {
  const trackingLink = order.barcode ? `${getEnv('VITE_REACT_APP_LANDING_URL')}/tracking/${order.barcode}` : ''

  const handleCopyTrackingLink = useCallback(() => {
    if (!trackingLink) return

    navigator.clipboard.writeText(trackingLink).then(() => {
      notify({ type: 'success', title: 'Ссылка на оплату скопирована' })
    })
  }, [trackingLink])

  const handleDownloadInvoice = useCallback(() => {
    if (!isDocumentInvoiceLoading && docInvoice) {
      const { blob } = docInvoice
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Счёт на оплату №${order.barcode}`
      link.click()
      URL.revokeObjectURL(url)
    }
  }, [docInvoice, isDocumentInvoiceLoading, order.barcode])

  return (
    <div>
      {/* Блок для ФЛ: онлайн-оплата + ссылка */}
      {order.access?.createPaymentTransaction && (
        <>
          <div className="mb-2 text-headline-sm">Оплата</div>

          <div className="mb-4 mt-2">
            <div className="mb-4 text-body-sm text-on-surface-variant">
              Для этой заявки доступна онлайн-оплата от физических лиц. Оплатите заявку самостоятельно или поделитесь
              ссылкой на оплату с плательщиком.
            </div>

            <PaymentForm id={order.id} />

            {order.barcode && (
              <div className="mt-4">
                <div className="flex items-center gap-[1px]">
                  <a className="link after:hidden" href={trackingLink} rel="noreferrer" target="_blank">
                    Поделиться ссылкой на оплату с плательщиком
                  </a>
                  <IconButton onClick={handleCopyTrackingLink}>
                    <IconFileCopy size="xs" />
                  </IconButton>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Блок для ЮЛ/ИП: счёт на оплату */}
      {order.access?.viewInvoice && (
        <>
          <div className="mb-2 text-headline-sm">Оплата</div>

          <div className="mb-4 mt-2 flex flex-col gap-3">
            <div className="text-body-sm text-on-surface-variant">
              Оплата принимается только от участников перевозки: заказчика, отправителя или получателя груза. Оплата за
              ИП производится только с расчетного счёта ИП.
            </div>

            {!isDocumentInvoiceLoading ? (
              <a
                download
                className="link inline-flex cursor-pointer items-center gap-2"
                onClick={handleDownloadInvoice}
              >
                <IconFile size="xs" />
                <span>Счёт на оплату</span>
              </a>
            ) : (
              <PlaceholderCell className="!w-1/2 min-w-20" height={25} />
            )}
          </div>
        </>
      )}

      {/* История оплат/возвратов - показываем всегда, если есть */}
      {!!(order.barcode && payment?.length) && (
        <>
          {!order.access?.createPaymentTransaction && !order.access?.viewInvoice && (
            <div className="mb-2 text-headline-sm">Оплата</div>
          )}
          <OrderPaymentBlock
            isPaymentLoading={isPaymentLoading || isFetching}
            payment={payment}
            paymentStatus={order.paymentStatus}
            totalPaymentAmount={order.totalPrice}
          />
        </>
      )}

      {/* Блок для ФЛ: только история (частичная оплата/переплата) */}
      {order.access?.createPaymentTransaction && (
        <div className="text-on-surface-secondary text-body-sm">
          {order.paymentStatus === OrderItem.paymentStatus.FULL && 'Заявка оплачена полностью'}
          {order.paymentStatus === OrderItem.paymentStatus.OVER && 'По заявке есть переплата'}
        </div>
      )}
    </div>
  )
}
